import clsx from 'clsx'
import { useContext, type ReactNode, type Ref } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import FormatCurrency from 'components/Common/FormatCurrency'
import CurrencyContext from 'contexts/currencyContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import AccommodationRoomSelectionError, { AccommodationRoomSelectionErrorContext } from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationRoomSelectionError'
import { packageSelectionMobileFooterStyles } from 'components/AccommodationKit/AccommodationPackageSelection/packageSelectionMobileFooterStyles'
import PackageSelectionBurnPrice from 'components/OfferPage/packageSelection/PackageSelectionBurnPrice'
import type { PackageSelectionBurnQuote } from 'components/OfferPage/packageSelection/packageSelectionBurnQuote'
import AccommodationPackagePriceSkeleton from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackagePriceSkeleton'

const Summary = styled.div`
  &.rail {
    display: none;
    gap: ${rem(12)};
    padding: ${rem(20)} ${rem(16)};
    ${mediaQueryUp.tablet} { display: grid; }
  }
  &.mobile-footer {
    ${packageSelectionMobileFooterStyles}
    gap: ${rem(16)};
    padding-block-start: ${rem(12)};
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'price' 'actions';
    &.has-error { grid-template-areas: 'price' 'error' 'actions'; }
    ${mediaQueryUp.tablet} { display: none; }
  }
`

const Count = styled(BodyText)`
  grid-area: count;
  ${mediaQueryUp.tablet} { grid-area: auto; }
`

const Price = styled.div`
  display: grid;
  grid-area: price;
  gap: 0;
  ${mediaQueryUp.tablet} { grid-area: auto; gap: ${rem(12)}; }
`

const Actions = styled.div`
  display: grid;
  gap: ${rem(12)};
  grid-area: actions;
  margin-block-start: ${rem(12)};
  .mobile-footer & { margin-block-start: 0; }
  ${mediaQueryUp.tablet} { grid-area: auto; margin-block-start: 0; }
`

interface Props {
  ref?: Ref<HTMLDivElement>
  presentation: 'rail' | 'mobile-footer'
  count: number
  duration?: number
  checkIn?: string
  price: number
  memberPrice: number
  hasMemberPrice: boolean
  offerConfig?: App.OfferLuxPlusConfig
  burnPayment?: Pick<PackageSelectionBurnQuote, 'regular' | 'member'>
  loading: boolean
  actions: ReactNode
  selection?: ReactNode
}

/** Feature-owned summary regions keep the rail and footer on the same resolved price. */
function FlashPackageSelectionSummary({ ref, presentation, count, duration, checkIn, price, memberPrice, hasMemberPrice, offerConfig, burnPayment, loading, actions, selection }: Props) {
  const intl = useIntl()
  const currency = useContext(CurrencyContext)
  const mobile = presentation === 'mobile-footer'
  const hasSelectionError = useContext(AccommodationRoomSelectionErrorContext)
  const roomCountLabel = intl.formatMessage({ id: 'IBUNcww58RxHVF60NTfl', defaultMessage: '{count, plural, one {# room} other {# rooms}} for', description: 'Caption above the selected Flash hotel package total showing how many guest rooms are selected' }, { count })
  const taxesLabel = intl.formatMessage({ id: 'hFMmwvVqQbv7zmQQcJna', defaultMessage: 'Includes taxes & fees', description: 'Tax and fee disclosure beneath the selected Flash hotel package total' })
  return <Summary ref={ref} tabIndex={-1} className={clsx(presentation, { empty: mobile && !count, 'has-error': hasSelectionError })} data-testid={mobile ? 'flash-option-d-mobile-summary' : 'flash-option-d-summary'}>
    {!mobile && <Count variant="medium" weight="bold">{roomCountLabel}</Count>}
    <Price aria-live="polite" aria-busy={loading}>
      {mobile && !!duration && <Caption variant="smallalt">{checkIn ? intl.formatMessage({
        id: 'N86TPQ2cT7LFRL7oOQxt',
        defaultMessage: '{count, plural, one {# room} other {# rooms}} for {duration, plural, one {# night} other {# nights}}',
        description: 'Mobile sticky footer caption combining the selected Flash hotel rooms and exact dated stay length before the booking total',
      }, { count, duration }) : intl.formatMessage({
        id: 'hDvU1WsjwJAsRqn9GQ_A',
        defaultMessage: '{count, plural, one {# room} other {# rooms}} for {duration, plural, one {# night} other {# nights}} from',
        description: 'Mobile sticky footer caption combining the selected Flash hotel room count and stay length before the package total',
      }, { count, duration })}
      </Caption>}
      {mobile && !duration && <Caption variant="smallalt">{roomCountLabel}</Caption>}
      {loading && <AccommodationPackagePriceSkeleton compact />}
      {!loading && burnPayment && <PackageSelectionBurnPrice price={price} memberPrice={memberPrice} payment={burnPayment} offerConfig={offerConfig} />}
      {!loading && !burnPayment && hasMemberPrice && <LuxPlusPriceStack price={price} memberPrice={memberPrice} saleUnit="total" size="M" offerConfig={offerConfig} />}
      {!loading && !burnPayment && !hasMemberPrice && mobile && <Heading variant="heading5">{intl.formatMessage({
        id: 'LN9BtHT5_pZ2y5kqRCAF',
        defaultMessage: '{price} total',
        description: 'Mobile sticky footer heading for the selected Flash hotel package price, with the total label beside the amount',
      }, { price: <FormatCurrency value={price} currency={currency} format="roundedDollar" /> })}
      </Heading>}
      {!loading && !burnPayment && !hasMemberPrice && !mobile && <Heading variant="heading4"><FormatCurrency value={price} currency={currency} format="roundedDollar" /></Heading>}
      {mobile && <Caption variant="smallalt" colour="neutral-three">{taxesLabel}</Caption>}
      {!mobile && <Caption variant="medium">{taxesLabel}</Caption>}
    </Price>
    <AccommodationRoomSelectionError />
    <Actions>{actions}</Actions>
    {selection}
  </Summary>
}

export default FlashPackageSelectionSummary
